import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  collection, onSnapshot, doc, setDoc, query, where
} from "firebase/firestore";
import {
  Save, Upload, Check, X, Download, Clock, Loader2, FileText, Trash2
} from "lucide-react";
import { db } from "../lib/firebase";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Switch } from "./ui/switch";
import { useToast } from "../hooks/use-toast";
import { useAuth } from "../context/AuthContext";
import { REGISTRATION_FIELDS, CLIENT_DOCS } from "../data/compliance";
import {
  uploadComplianceDoc, updateComplianceDoc, deleteComplianceDoc, expiryState
} from "../lib/compliance";

const STATUS = {
  pending: "bg-amber-100 text-amber-800",
  approved: "bg-emerald-100 text-emerald-700",
  rejected: "bg-red-100 text-red-700",
  acknowledged: "bg-sky-100 text-sky-700"
};

export default function AdminCompliance() {
  const { user } = useAuth();
  const { toast } = useToast();
  const [regs, setRegs] = useState({});
  const [savingRegs, setSavingRegs] = useState(false);
  const [docs, setDocs] = useState([]);
  const [busy, setBusy] = useState("");
  const clientFileRefs = useRef({});

  useEffect(() => {
    const unsubR = onSnapshot(doc(db, "site_compliance", "registrations"), (snap) => {
      if (snap.exists()) setRegs(snap.data());
    }, (e) => console.warn("regs read:", e?.message));
    const unsubD = onSnapshot(collection(db, "compliance_documents"),
      (snap) => setDocs(snap.docs.map((d) => ({ id: d.id, ...d.data() }))),
      (e) => console.warn("docs read:", e?.message));
    return () => { unsubR(); unsubD(); };
  }, []);

  const driverDocs = useMemo(() => docs.filter((d) => d.category === "driver"), [docs]);
  const clientDocs = useMemo(() => {
    const m = {};
    docs.filter((d) => d.category === "client").forEach((d) => { m[d.docKey] = d; });
    return m;
  }, [docs]);

  const driverSummary = useMemo(() => {
    const m = {};
    driverDocs.forEach((d) => {
      const name = d.ownerName || d.ownerUid || "Unknown";
      if (!m[name]) m[name] = { total: 0, approved: 0, expiring: 0, expired: 0 };
      m[name].total += 1;
      if (d.status === "approved") m[name].approved += 1;
      const e = expiryState(d.expiresAt);
      if (e.expired) m[name].expired += 1;
      else if (e.soon) m[name].expiring += 1;
    });
    return m;
  }, [driverDocs]);

  const setReg = (key, patch) => setRegs((r) => ({ ...r, [key]: { ...(r[key] || {}), ...patch } }));

  const saveRegs = async () => {
    setSavingRegs(true);
    try {
      await setDoc(doc(db, "site_compliance", "registrations"), regs, { merge: true });
      toast({ title: "Registrations saved", description: "Public page updates instantly for active items." });
    } catch (err) {
      toast({ title: "Couldn't save", description: err?.message });
    } finally {
      setSavingRegs(false);
    }
  };

  const uploadClientDoc = async (docDef, file) => {
    if (!file) return;
    setBusy(docDef.key);
    try {
      await uploadComplianceDoc({
        file, category: "client", docKey: docDef.key, label: docDef.label,
        ownerUid: null, uploadedBy: user?.id, uploadedByRole: "admin", status: "approved"
      });
      toast({ title: "Uploaded", description: `${docDef.label} published to clients.` });
    } catch (err) {
      toast({ title: "Upload failed", description: err?.message });
    } finally {
      setBusy("");
    }
  };

  const review = async (id, patch) => {
    try {
      await updateComplianceDoc(id, patch);
      toast({ title: "Updated" });
    } catch (err) {
      toast({ title: "Couldn't update", description: err?.message });
    }
  };

  const removeDoc = async (id) => {
    if (!window.confirm("Delete this document record?")) return;
    try { await deleteComplianceDoc(id); toast({ title: "Deleted" }); }
    catch (err) { toast({ title: "Couldn't delete", description: err?.message }); }
  };

  const exportReport = () => {
    const lines = [
      "OBLED Corporation d/b/a 518 Courier — Compliance Report",
      `Generated: ${new Date().toLocaleString()}`,
      "",
      "== Registrations ==",
      ...REGISTRATION_FIELDS.map((f) => `${f.label}: ${regs[f.key]?.value || "—"} ${regs[f.key]?.active ? "(active/public)" : "(hidden)"}`),
      "",
      "== Driver documents ==",
      ...driverDocs.map((d) => `${d.ownerName || d.ownerUid} · ${d.label} · ${d.status} · ${expiryState(d.expiresAt).label}${d.notes ? " · note: " + d.notes : ""}`),
      "",
      "== Shared client documents ==",
      ...CLIENT_DOCS.map((d) => `${d.label}: ${clientDocs[d.key] ? "published" : "not uploaded"}`)
    ];
    const blob = new Blob([lines.join("\n")], { type: "text/plain;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `518-compliance-report-${Date.now()}.txt`;
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-6" data-testid="admin-compliance">
      {/* Registrations */}
      <div className="bg-white rounded-2xl border border-slate-200 p-6">
        <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
          <div>
            <h3 className="font-display font-bold text-[#0f2847]">Government registrations</h3>
            <p className="text-xs text-slate-500 mt-0.5">Enter official values. Toggle "Show publicly" only once verified — hidden by default.</p>
          </div>
          <Button onClick={saveRegs} disabled={savingRegs} data-testid="save-registrations" className="bg-[#0f2847] hover:bg-[#1e3a5f] text-white rounded-full h-10 px-5 font-semibold">
            {savingRegs ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Save className="w-4 h-4 mr-1.5" /> Save</>}
          </Button>
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          {REGISTRATION_FIELDS.map((f) => (
            <div key={f.key} className="rounded-xl border border-slate-200 p-4">
              <label className="text-xs font-bold text-slate-600 uppercase tracking-wide">{f.label}</label>
              <Input
                value={regs[f.key]?.value || ""}
                onChange={(e) => setReg(f.key, { value: e.target.value })}
                placeholder="Enter official value"
                data-testid={`reg-input-${f.key}`}
                className="h-10 mt-1.5 rounded-lg border-slate-200"
              />
              <div className="mt-3 flex items-center gap-2">
                <Switch
                  checked={!!regs[f.key]?.active}
                  onCheckedChange={(v) => setReg(f.key, { active: v })}
                  data-testid={`reg-toggle-${f.key}`}
                  className="data-[state=checked]:bg-emerald-500"
                />
                <span className="text-xs font-semibold text-slate-600">Show publicly</span>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Shared client documents */}
      <div className="bg-white rounded-2xl border border-slate-200 p-6">
        <h3 className="font-display font-bold text-[#0f2847] mb-1">Shared client documents</h3>
        <p className="text-xs text-slate-500 mb-4">Uploaded files are published to authorized clients in the Compliance Document Center.</p>
        <div className="grid md:grid-cols-2 gap-3">
          {CLIENT_DOCS.map((d) => {
            const cur = clientDocs[d.key];
            return (
              <div key={d.key} className="flex items-center gap-3 rounded-xl border border-slate-200 p-4">
                <FileText className="w-5 h-5 text-[#0f2847] shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-[#0f2847] text-sm">{d.label}</div>
                  <div className="text-xs text-slate-500">{cur ? "Published" : "Not uploaded"}</div>
                </div>
                {cur?.fileUrl && (
                  <a href={cur.fileUrl} target="_blank" rel="noreferrer" className="text-[#0f2847] hover:text-[#f59321]"><Download className="w-4 h-4" /></a>
                )}
                <input type="file" className="hidden" ref={(el) => (clientFileRefs.current[d.key] = el)} onChange={(e) => uploadClientDoc(d, e.target.files?.[0])} />
                <Button onClick={() => clientFileRefs.current[d.key]?.click()} disabled={busy === d.key} size="sm" data-testid={`client-upload-${d.key}`} className="bg-[#f59321] hover:bg-[#e07f10] text-white rounded-full h-8 px-3 text-xs font-semibold">
                  {busy === d.key ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <><Upload className="w-3.5 h-3.5 mr-1" /> {cur ? "Replace" : "Upload"}</>}
                </Button>
              </div>
            );
          })}
        </div>
      </div>

      {/* Driver compliance status */}
      <div className="bg-white rounded-2xl border border-slate-200 p-6">
        <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
          <h3 className="font-display font-bold text-[#0f2847]">Driver compliance status</h3>
          <Button onClick={exportReport} variant="outline" size="sm" data-testid="export-compliance-report" className="rounded-full h-9 px-4 font-semibold border-2">
            <Download className="w-4 h-4 mr-1.5" /> Export report
          </Button>
        </div>
        {Object.keys(driverSummary).length === 0 ? (
          <p className="text-sm text-slate-500">No driver documents submitted yet.</p>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3 mb-2">
            {Object.entries(driverSummary).map(([name, s]) => (
              <div key={name} className="rounded-xl border border-slate-200 p-4">
                <div className="font-semibold text-[#0f2847] truncate">{name}</div>
                <div className="mt-1 text-xs text-slate-600">{s.approved}/{s.total} approved</div>
                <div className="mt-1 flex gap-2 text-xs">
                  {s.expiring > 0 && <span className="text-amber-600 font-semibold">{s.expiring} expiring</span>}
                  {s.expired > 0 && <span className="text-red-600 font-semibold">{s.expired} expired</span>}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Driver document review */}
      <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
        <div className="px-6 py-4 border-b border-slate-200">
          <h3 className="font-display font-bold text-[#0f2847]">Driver document review</h3>
          <p className="text-xs text-slate-500 mt-0.5">Approve or reject, set expiration dates and add private notes.</p>
        </div>
        {driverDocs.length === 0 ? (
          <div className="p-10 text-center text-sm text-slate-500">No driver documents yet.</div>
        ) : (
          <div className="divide-y divide-slate-100">
            {driverDocs.map((d) => {
              const exp = expiryState(d.expiresAt);
              return (
                <div key={d.id} className="p-5 flex flex-wrap items-center gap-4" data-testid={`review-doc-${d.id}`}>
                  <div className="flex-1 min-w-[200px]">
                    <div className="font-semibold text-[#0f2847]">{d.ownerName || d.ownerUid} · {d.label}</div>
                    <div className="mt-1 flex flex-wrap items-center gap-2 text-xs">
                      <span className={`px-2 py-0.5 rounded-full font-bold ${STATUS[d.status] || STATUS.pending}`}>{d.status}</span>
                      <span className={`font-semibold ${exp.cls}`}><Clock className="w-3 h-3 inline -mt-0.5 mr-1" />{exp.label}</span>
                      {d.fileUrl && <a href={d.fileUrl} target="_blank" rel="noreferrer" className="text-[#f59321] font-semibold underline">View file</a>}
                    </div>
                  </div>
                  <input
                    type="date"
                    defaultValue={d.expiresAt ? d.expiresAt.slice(0, 10) : ""}
                    onChange={(e) => review(d.id, { expiresAt: e.target.value ? new Date(e.target.value).toISOString() : null })}
                    data-testid={`review-expiry-${d.id}`}
                    className="h-9 rounded-lg border border-slate-200 px-2 text-sm"
                  />
                  <Input
                    defaultValue={d.notes || ""}
                    onBlur={(e) => e.target.value !== (d.notes || "") && review(d.id, { notes: e.target.value })}
                    placeholder="Private note"
                    data-testid={`review-note-${d.id}`}
                    className="h-9 w-40 rounded-lg border-slate-200 text-sm"
                  />
                  <div className="flex items-center gap-2">
                    <Button onClick={() => review(d.id, { status: "approved" })} size="sm" data-testid={`approve-${d.id}`} className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-full h-9 px-3">
                      <Check className="w-4 h-4" />
                    </Button>
                    <Button onClick={() => review(d.id, { status: "rejected" })} size="sm" variant="outline" data-testid={`reject-${d.id}`} className="border-red-200 text-red-600 hover:bg-red-50 rounded-full h-9 px-3">
                      <X className="w-4 h-4" />
                    </Button>
                    <button onClick={() => removeDoc(d.id)} className="text-slate-400 hover:text-red-600"><Trash2 className="w-4 h-4" /></button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
